import { useReferral } from "../contexts/ReferralContext";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, X } from "lucide-react";
import { useState } from "react";

/**
 * WelcomeBanner - Shows a personalized welcome message for referred users
 * "Welcome from [Name]" - displayed at the top of login/register pages
 */
export const WelcomeBanner = ({ className = "" }) => {
  const { referralData, hasReferral, clearReferral } = useReferral();
  const [dismissed, setDismissed] = useState(false);

  if (!hasReferral || dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    // Don't clear referral - just hide the banner
    // The code will still be used for registration
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4 }}
        className={`relative ${className}`}
        data-testid="welcome-banner"
      >
        <div className="bg-gradient-to-r from-[#4A6741] to-[#5A7D4F] rounded-2xl p-5 text-white shadow-lg shadow-[#4A6741]/20">
          {/* Dismiss button */}
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Dismiss"
          >
            <X size={16} className="text-white/70" />
          </button>

          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className="w-12 h-12 bg-white/15 rounded-full flex items-center justify-center flex-shrink-0">
              <Gift className="text-white" size={22} />
            </div>

            {/* Content */}
            <div className="flex-1 pr-6">
              <p className="text-white/70 text-sm mb-1">
                {referralData?.partnerInvitation ? "From" : "You've been invited by"}
              </p>
              <h3 className="font-heading text-xl text-white mb-2">
                {referralData?.referrerName || "A friend"}
              </h3>
              {referralData?.partnerInvitation ? (
                <p
                  className="text-white text-base leading-relaxed italic"
                  data-testid="welcome-banner-invitation"
                  style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "1.05rem" }}
                >
                  {`“${referralData.partnerInvitation}”`}
                </p>
              ) : (
                <p className="text-white/80 text-sm leading-relaxed">
                  Welcome to Reclaim — School of Consciousness.
                  <br />
                  Begin your journey to presence and inner peace.
                </p>
              )}
            </div>
          </div>

          {/* Decorative element */}
          <div className="absolute bottom-0 right-0 w-32 h-32 opacity-10">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <circle cx="80" cy="80" r="60" fill="white" />
            </svg>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WelcomeBanner;
